import React from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../../generic/hooks/useDateFormatter'
import { EMPTY_VALUE, boundedRatio, formatQuantityWithUnit, isPartComponent } from '../../../utils/jobDetailFormat'
import { materialLineComplete, materialPending } from '../../../utils/jobDetailFootprint'
import { HeaderCell, ProgressBar } from '../JobDetailPrimitives'
import { SystemValueChip } from '../../../../../../../generic/systemValues/SystemValueChip'

// Historia 169 (RN17, C3, CU2), enmienda UX-9 (03-dominio.md, tras la revisión del PO en mic1) — D6a
// se pinta con la MISMA tabla que la pestaña Materiales del job (#160 S70-S72, S81,
// `tabs/MaterialsTab.jsx`): Código · Material · Requerido · Consumido · Pendiente · Avance, con los
// VALORES DE ESTA PIEZA (`perPieceRequired`, `consumed`; pendiente = requerido − consumido,
// `materialPending`) + un chip de estado por fila (S78). Debajo de cada material, sus consumos
// (materia prima · lote o "Sin lote" · cantidad · fecha con año, UX-3).
//
// Los componentes pieza (`PART_REFERENCE`, #160 E-7/S81) son la ÚNICA excepción al "—" del panel: "—"
// en consumido y pendiente con el tooltip "No consumption record", sin barra, sin "Completo" ni color
// de aviso, sin consumos. Cualquier OTRA celda sin dato (p.ej. requerido/pendiente/avance de una
// OUT_OF_LIST sin `perPieceRequired`) se deja VACÍA, nunca "—" (UX-9, precisión (5)).

// Story 204 (RN1.5): every status is translated by its own code; only PENDING keeps its legacy key (P1-bis: the code
// PENDING reads differently on other screens).
const STATUS_KEY = {
  PENDING: 'Not mounted'
}

const STATUS_TONE = { MOUNTED: 'success', PARTIAL: 'warning' }

const GRID = 'minmax(0, 0.9fr) minmax(0, 2fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.2fr)'

const isPresent = (value) => value !== null && value !== undefined

const WithoutRecord = ({ testId }) => {
  const { t_ } = useSmartTranslation()
  return (
    <Tooltip title={t_('No consumption record')}>
      <Typography data-testid={testId} sx={{ textAlign: 'right', color: 'text.secondary' }}>{EMPTY_VALUE}</Typography>
    </Tooltip>
  )
}
WithoutRecord.propTypes = { testId: PropTypes.string }

const MaterialRow = ({ line, language }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const code = line.code
  const partComponent = isPartComponent(line)
  const complete = !partComponent && materialLineComplete(line)
  const pending = materialPending(line)
  const showBar = !partComponent && isPresent(line.perPieceRequired)
  const consumptions = line.consumptions || []
  return (
    <Box data-testid={'footprint-material-' + code} sx={{ borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2, py: 1, minHeight: 54 }}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <SystemValueChip look='overline' domain='MaterialMountStatus' value={line.status} labelKeys={STATUS_KEY} tone={STATUS_TONE[line.status] || 'default'} testId={'footprint-material-status-' + code} />
          <Typography sx={{ fontFamily: 'monospace' }}>{code}</Typography>
        </Stack>
        <Typography noWrap title={line.name}>{line.name}</Typography>
        <Typography sx={{ textAlign: 'right' }}>{isPresent(line.perPieceRequired) ? formatQuantityWithUnit(line.perPieceRequired, line.unit, language) : ''}</Typography>
        {partComponent
          ? <WithoutRecord testId={'footprint-material-consumed-' + code} />
          : <Typography data-testid={'footprint-material-consumed-' + code} sx={{ textAlign: 'right' }}>{isPresent(line.consumed) ? formatQuantityWithUnit(line.consumed, line.unit, language) : ''}</Typography>}
        {partComponent
          ? <WithoutRecord testId={'footprint-material-pending-' + code} />
          : (
            <Typography
              data-testid={'footprint-material-pending-' + code}
              sx={{ textAlign: 'right', fontWeight: 800, color: complete ? 'primary.main' : 'yellow.main' }}
            >
              {pending === null ? '' : (complete ? t_('Complete') : formatQuantityWithUnit(pending, line.unit, language))}
            </Typography>
            )}
        <Box>
          {showBar ? <ProgressBar value={complete ? 100 : boundedRatio(line.consumed, line.perPieceRequired)} label={code} /> : null}
        </Box>
      </Box>
      {!partComponent && consumptions.length > 0
        ? (
          <Stack sx={{ px: 2, pb: 1 }} spacing={0.25}>
            {consumptions.map((consumption, index) => (
              <Typography key={consumption.rawMaterialId + '-' + index} variant='body2' color='text.secondary' data-testid={'footprint-consumption-' + consumption.rawMaterialId}>
                {consumption.rawMaterialCode}
                {' · '}
                {consumption.batch || t_('No batch')}
                {' · '}
                {formatQuantityWithUnit(consumption.quantity, line.unit, language)}
                {' · '}
                {consumption.date ? formatDateTime(consumption.date) : ''}
              </Typography>
            ))}
          </Stack>
          )
        : null}
    </Box>
  )
}

MaterialRow.propTypes = { line: PropTypes.object, language: PropTypes.string }

const FootprintMaterialsDetail = ({ data = {} }) => {
  const { t_, i18n } = useSmartTranslation()
  const language = i18n?.language || 'en'
  const lines = data.lines || []

  if (lines.length === 0) {
    return <Typography color='text.secondary'>{t_('No materials in this job')}</Typography>
  }

  return (
    <Box role='table' aria-label={t_('Job materials')}>
      <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <HeaderCell>{t_('Code')}</HeaderCell>
        <HeaderCell>{t_('Material')}</HeaderCell>
        <HeaderCell align='right'>{t_('Required quantity')}</HeaderCell>
        <HeaderCell align='right'>{t_('Consumed')}</HeaderCell>
        <HeaderCell align='right'>{t_('Pending quantity')}</HeaderCell>
        <HeaderCell>{t_('Advance')}</HeaderCell>
      </Box>
      {lines.map((line) => <MaterialRow key={line.referenceId + '-' + line.code} line={line} language={language} />)}
    </Box>
  )
}

FootprintMaterialsDetail.propTypes = { data: PropTypes.object }

export default React.memo(FootprintMaterialsDetail)
